import { useForm, getFormProps } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import { invariantResponse } from '@epic-web/invariant'
import { type ActionFunctionArgs } from '@remix-run/node'
import { redirect, useFetcher, useFetchers } from '@remix-run/react'
import { safeRedirect } from 'remix-utils/safe-redirect'
import { z } from 'zod'
import { Icon } from '#app/components/ui/icon.tsx'
import { useHints } from '#app/utils/client-hints.tsx'
import {
	useOptionalRequestInfo,
	useRequestInfo,
} from '#app/utils/request-info.ts'
import { type Theme, setTheme } from '#app/utils/theme.server.ts'

const ThemeFormSchema = z.object({
	theme: z.enum(['system', 'light', 'dark']),
	// this is useful for progressive enhancement
	redirectTo: z.string().optional(),
})

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	const submission = parseWithZod(formData, {
		schema: ThemeFormSchema,
	})

	invariantResponse(submission.status === 'success', 'Invalid theme received')

	const { theme, redirectTo } = submission.value

	const responseInit = {
		headers: { 'set-cookie': setTheme(theme) },
	}

	// If redirectTo is provided (progressive enhancement for non-JS), redirect
	// Otherwise return JSON for fetcher requests (no page reload)
	if (redirectTo) {
		// safeRedirect guards against an open redirect via the form-supplied value.
		return redirect(safeRedirect(redirectTo), responseInit)
	} else {
		return new Response(JSON.stringify({ result: submission.reply({}) }), {
			headers: {
				...responseInit.headers,
				'Content-Type': 'application/json',
			},
		})
	}
}

export function ThemeSwitch({
	userPreference,
	showLabel = false,
}: {
	userPreference?: Theme | null
	showLabel?: boolean
}) {
	const fetcher = useFetcher<typeof action>()

	const [form] = useForm({
		id: 'theme-switch',
		// lastResult is optional and we're using optimistic updates instead
	})

	const optimisticMode = useOptimisticThemeMode()
	const mode = optimisticMode ?? userPreference ?? 'system'
	const nextMode =
		mode === 'system' ? 'light' : mode === 'light' ? 'dark' : 'system'
	const modeLabel = {
		light: (
			<Icon name="sun">
				<span className={showLabel ? '' : 'sr-only'}>High-contrast</span>
			</Icon>
		),
		dark: (
			<Icon name="moon">
				<span className={showLabel ? '' : 'sr-only'}>Default</span>
			</Icon>
		),
		system: (
			<Icon name="laptop">
				<span className={showLabel ? '' : 'sr-only'}>System</span>
			</Icon>
		),
	}

	return (
		<fetcher.Form
			method="POST"
			{...getFormProps(form)}
			action="/resources/theme-switch"
		>
			<input type="hidden" name="theme" value={nextMode} />
			<div className="flex">
				<button
					type="submit"
					className="flex h-8 w-8 cursor-pointer items-center justify-center"
				>
					{modeLabel[mode]}
				</button>
			</div>
		</fetcher.Form>
	)
}

/**
 * If the user's changing their theme mode preference, this will return the
 * value it's being changed to.
 */
export function useOptimisticThemeMode() {
	const fetchers = useFetchers()
	const themeFetcher = fetchers.find(
		(f) => f.formAction === '/resources/theme-switch',
	)

	if (themeFetcher && themeFetcher.formData) {
		const submission = parseWithZod(themeFetcher.formData, {
			schema: ThemeFormSchema,
		})

		if (submission.status === 'success') {
			return submission.value.theme
		}
	}
}

/**
 * @returns the user's theme preference, or the client hint theme if the user
 * has not set a preference.
 */
export function useTheme() {
	const hints = useHints()
	const requestInfo = useRequestInfo()
	const optimisticMode = useOptimisticThemeMode()
	if (optimisticMode) {
		return optimisticMode === 'system' ? hints.theme : optimisticMode
	}
	return requestInfo.userPrefs.theme ?? hints.theme
}

/**
 * Like `useTheme`, but returns `undefined` instead of throwing when no root
 * loader data is available (e.g. `root.tsx`'s `ErrorBoundary` renders
 * without loader data). For components that must degrade gracefully outside
 * a fully-hydrated route tree — callers should supply their own fallback
 * (e.g. `useOptionalTheme() ?? 'light'`). Mirrors `useOptionalRequestInfo`
 * (`#app/utils/request-info.ts`), which this is built on.
 */
export function useOptionalTheme() {
	const requestInfo = useOptionalRequestInfo()
	const optimisticMode = useOptimisticThemeMode()
	if (optimisticMode) {
		return optimisticMode === 'system'
			? requestInfo?.hints.theme
			: optimisticMode
	}
	return requestInfo?.userPrefs.theme ?? requestInfo?.hints.theme
}
